import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../../../../../../firebaseConfig";
import { useUserStore } from "../../../../../../../store/userStore";
import type { VehicleSocket } from "../../../../../../../store/admin/vehiclesSocketStore";
import api, { isAxiosClientTimeout } from "../../../../../../../utils/api";
import type { Command } from "../../../../../../../store/reseller-admin/brandsStore";
import {
  useDeviceCommandsStore,
  getCommandStatusLabel,
  COMMAND_STATUS_COLORS,
  isSuccessStatus,
  type CommandStatus,
  type SocketCommand,
} from "../../../../../../../store/admin/deviceCommandsStore";
import { useEscapeKey } from "../../../../../../../hooks/useEscapeKey";

// Tipos para la respuesta del API
interface CommandResponse {
  ok: boolean;
  command: {
    request_id: string;
    device_id: string;
    imei?: string;
    company_id?: string;
    vehicle_id?: string;
    remolque_id?: string;
    brand_id?: string;
    brand?: string;
    model?: string;
    command_doc_id?: string;
    command_code: string;
    command_name?: string;
    status: CommandStatus;
    error?: string;
    raw_request?: Record<string, unknown>;
    raw_response?: Record<string, unknown>;
    sent_at?: number;
    finished_at?: number;
    created_at?: number;
    updated_at?: number;
  };
  wait_timeout?: boolean;
}

interface CommandExecuteModalProps {
  vehicle: VehicleSocket;
  command: Command;
  isDarkMode: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Cuando la petición HTTP expira por tiempo; el backend puede seguir procesando */
  onSendCommandClientTimeout?: () => void;
}

type ViewState = "confirm" | "sending" | "result";
type CommandChannel = "gprs" | "sms";

export default function CommandExecuteModal({
  vehicle,
  command,
  isDarkMode,
  onClose,
  onSuccess,
  onSendCommandClientTimeout,
}: CommandExecuteModalProps) {
  useEscapeKey(onClose);
  const { t } = useTranslation("lite");
  const user = useUserStore((state) => state.user);

  const smsEnabled = command.sms_enabled === true;
  const smsBody = command.sms_body || command.transport?.value || "";

  const [viewState, setViewState] = useState<ViewState>("confirm");

  const [channel, setChannel] = useState<CommandChannel>("gprs");
  const [smsPassword, setSmsPassword] = useState("");
  const [confirmError, setConfirmError] = useState<string | null>(null);

  // Auth states
  const [authPassword, setAuthPassword] = useState("");
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showSmsPassword, setShowSmsPassword] = useState(false);

  // Command response state
  const [commandResponse, setCommandResponse] = useState<CommandResponse | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);

  // Store - obtener comandos del dispositivo actual
  const addCommand = useDeviceCommandsStore((state) => state.addCommand);
  const commandsByDevice = useDeviceCommandsStore((state) => state.commandsByDevice);

  // Buscar el comando actual en el store (se actualiza automáticamente vía socket)
  const currentCommandFromStore = useMemo<SocketCommand | null>(() => {
    if (!currentRequestId || !vehicle?.id) return null;
    const deviceCommands = commandsByDevice[vehicle.id] || [];
    return deviceCommands.find((cmd) => cmd.request_id === currentRequestId) || null;
  }, [commandsByDevice, vehicle?.id, currentRequestId]);

  // Theme classes
  const bgColor = isDarkMode ? "bg-dark-bg" : "bg-white";
  const textColor = isDarkMode ? "text-gray-300" : "text-gray-700";
  const textSecondary = isDarkMode ? "text-gray-400" : "text-gray-500";
  const borderColor = isDarkMode ? "border-gray-700" : "border-gray-200";
  const inputBg = isDarkMode ? "bg-[#252525]" : "bg-white";

  const handleConfirmSend = async () => {
    setConfirmError(null);

    if (command.password_required && !authPassword.trim()) {
      setConfirmError(t("deviceCommands.authRequired"));
      return;
    }
    if (channel === "sms" && !smsPassword.trim()) {
      setConfirmError(t("deviceCommands.smsPasswordRequired"));
      return;
    }

    if (command.password_required) {
      const email = user?.email;
      if (!email) {
        setConfirmError(t("deviceCommands.noEmail"));
        return;
      }

      setIsAuthenticating(true);
      try {
        await signInWithEmailAndPassword(auth, email, authPassword);
      } catch (err: any) {
        console.error("Auth error:", err);
        if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
          setConfirmError(t("deviceCommands.wrongPassword"));
        } else {
          setConfirmError(t("deviceCommands.authError"));
        }
        setIsAuthenticating(false);
        return;
      }
      setIsAuthenticating(false);
    }

    handleSendCommand();
  };

  // Handle send command
  const handleSendCommand = async () => {
    setViewState("sending");
    setApiError(null);
    setCommandResponse(null);
    setCurrentRequestId(null);

    try {
      const response =
        channel === "sms"
          ? await api.post("/sendCommandSms", {
              deviceId: vehicle.id,
              imei: vehicle.imei,
              command_code: command.code,
              password_sms: smsPassword,
              args: {},
              created_by_user_id: user?.id,
            })
          : await api.post("/sendCommand", {
              deviceId: vehicle.id,
              imei: vehicle.imei,
              command_code: command.code,
              args: {},
              await_ms: 8000,
              created_by_user_id: user?.id,
            });

      const data: CommandResponse = response.data;
      setCommandResponse(data);

      // Guardar request_id para escuchar actualizaciones del socket
      if (data.command?.request_id) {
        setCurrentRequestId(data.command.request_id);
      }

      // Guardar en el store apenas llega la respuesta HTTP (no esperar al socket).
      // El socket luego actualiza la misma fila vía dedupe por request_id.
      if (data.command?.request_id) {
        addCommand({
          device_id: data.command.device_id || vehicle.id,
          company_id: data.command.company_id || null,
          vehicle_id: data.command.vehicle_id || null,
          remolque_id: data.command.remolque_id || null,
          command: {
            request_id: data.command.request_id,
            imei: data.command.imei,
            brand_id: data.command.brand_id,
            brand: data.command.brand,
            model: data.command.model,
            command_doc_id: data.command.command_doc_id,
            command_code: data.command.command_code,
            command_name: data.command.command_name || command.name,
            status: data.command.status,
            error: data.command.error,
            raw_request: data.command.raw_request,
            raw_response: data.command.raw_response,
            sent_at: data.command.sent_at,
            finished_at: data.command.finished_at,
          },
        });
      }

      setViewState("result");
    } catch (err: unknown) {
      console.error("Error sending command:", err);
      if (isAxiosClientTimeout(err) && onSendCommandClientTimeout) {
        onSendCommandClientTimeout();
        return;
      }
      const anyErr = err as { response?: { data?: { message?: string } }; message?: string };
      setApiError(
        anyErr.response?.data?.message ||
          anyErr.message ||
          "Error al enviar comando",
      );
      setViewState("result");
    }
  };

  // Obtener color del estado
  const getStatusColor = (status: CommandStatus) => {
    const colors = COMMAND_STATUS_COLORS[status];
    if (!colors) return { bg: "bg-gray-100", text: "text-gray-700" };
    return isDarkMode
      ? { bg: colors.darkBg, text: colors.darkText }
      : { bg: colors.bg, text: colors.text };
  };

  // Render confirmation view
  if (viewState === "confirm") {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className={`${bgColor} rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto border ${borderColor} shadow-2xl`}>
          <div className={`flex items-center justify-between p-4 border-b ${borderColor}`}>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-send-plane-line text-black text-lg"></i>
              </div>
              <h2 className={`font-semibold text-lg ${textColor}`}>
                {t("deviceCommands.confirmTitle")}
              </h2>
            </div>
            <button
              onClick={onClose}
              className={`p-2 ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"} rounded-lg transition-colors`}
            >
              <i className={`ri-close-line text-xl ${textColor}`}></i>
            </button>
          </div>

          <div className="p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 shrink-0 rounded-lg bg-primary-500/10 flex items-center justify-center">
                <i className="ri-terminal-line text-primary-500 text-lg"></i>
              </div>
              <div className="min-w-0">
                <h3 className={`font-bold text-base ${textColor} truncate`}>{command.name}</h3>
                <p className={`text-xs ${textSecondary} truncate`}>
                  {t("deviceCommands.toUnit")} {vehicle.vehicle.name}
                </p>
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-terminal-line text-green-400"></i>
                <p className={`text-xs ${textSecondary}`}>{t("deviceCommands.commandMessage")}</p>
              </div>
              <div className="bg-[#1e1e1e] rounded-lg p-3 border border-gray-600">
                <code className="text-sm text-green-400 font-mono break-all">{channel === "sms" ? smsBody : command.transport?.value}</code>
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-terminal-line text-green-400"></i>
                <p className={`text-xs ${textSecondary}`}>{t("deviceCommands.codeMessage")}</p>
              </div>
              <div className="bg-[#1e1e1e] rounded-lg p-3 border border-gray-600">
                <code className="text-sm text-green-400 font-mono break-all">{command.code}</code>
              </div>
            </div>

            <div className="mb-4">
              <p className={`text-xs ${textSecondary} mb-2`}>{t("deviceCommands.channel")}</p>
              {smsEnabled ? (
                <div className="flex gap-3">
                  <label className={`flex-1 flex items-center justify-center gap-2 cursor-pointer rounded-lg border px-4 py-2.5 transition-colors ${channel === "gprs" ? "border-primary-500 bg-primary-500/10" : borderColor}`}>
                    <input
                      type="radio"
                      name="commandChannel"
                      value="gprs"
                      checked={channel === "gprs"}
                      onChange={() => { setChannel("gprs"); setConfirmError(null); }}
                      className="w-4 h-4 accent-primary-500"
                    />
                    <span className={textColor}>{t("deviceCommands.channelGprs")}</span>
                  </label>
                  <label className={`flex-1 flex items-center justify-center gap-2 cursor-pointer rounded-lg border px-4 py-2.5 transition-colors ${channel === "sms" ? "border-primary-500 bg-primary-500/10" : borderColor}`}>
                    <input
                      type="radio"
                      name="commandChannel"
                      value="sms"
                      checked={channel === "sms"}
                      onChange={() => { setChannel("sms"); setConfirmError(null); }}
                      className="w-4 h-4 accent-primary-500"
                    />
                    <span className={textColor}>{t("deviceCommands.channelSms")}</span>
                  </label>
                </div>
              ) : (
                <div className={`flex items-center gap-2 rounded-lg border ${borderColor} px-4 py-2.5 ${isDarkMode ? "bg-dark-surface" : "bg-gray-50"}`}>
                  <i className="ri-information-line text-primary-500"></i>
                  <span className={`text-xs ${textSecondary}`}>{t("deviceCommands.gprsOnly")}</span>
                </div>
              )}
            </div>

            {command.password_required && (
              <div className="mb-4">
                <label className={`block text-sm ${textColor} mb-2`}>
                  <i className="ri-shield-keyhole-line mr-1 text-yellow-500"></i>
                  <span className="text-red-500">*</span> {t("deviceCommands.password")}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={authPassword}
                    onChange={(e) => { setAuthPassword(e.target.value); setConfirmError(null); }}
                    placeholder={t("deviceCommands.passwordPlaceholder")}
                    className={`w-full px-4 py-2.5 pr-10 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${textSecondary} hover:${textColor}`}
                  >
                    <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                  </button>
                </div>
              </div>
            )}

            {channel === "sms" && (
              <div className="mb-4">
                <label className={`block text-sm ${textColor} mb-2`}>
                  <i className="ri-shield-keyhole-line mr-1 text-yellow-500"></i>
                  <span className="text-red-500">*</span> {t("deviceCommands.smsPassword")}
                </label>
                <div className="relative">
                  <input
                    type={showSmsPassword ? "text" : "password"}
                    value={smsPassword}
                    onChange={(e) => { setSmsPassword(e.target.value); setConfirmError(null); }}
                    placeholder={t("deviceCommands.smsPasswordPlaceholder")}
                    className={`w-full px-4 py-2.5 pr-10 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowSmsPassword(!showSmsPassword)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${textSecondary} hover:${textColor}`}
                  >
                    <i className={showSmsPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                  </button>
                </div>
                <p className={`text-xs ${textSecondary} mt-1`}>
                  {t("deviceCommands.smsPasswordHelp")}
                </p>
              </div>
            )}

            {confirmError && (
              <div className={`${isDarkMode ? "bg-red-900/30 text-red-400" : "bg-red-100 text-red-700"} px-3 py-2 rounded-lg text-sm mb-4`}>
                {confirmError}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className={`px-6 py-2.5 rounded-lg border ${borderColor} ${textColor} ${isDarkMode ? "hover:bg-dark-surface" : "hover:bg-gray-100"} transition-colors font-medium`}
              >
                {t("deviceCommands.cancel")}
              </button>
              <button
                onClick={handleConfirmSend}
                disabled={isAuthenticating || (command.password_required && !authPassword.trim()) || (channel === "sms" && !smsPassword.trim())}
                className="px-6 py-2.5 rounded-lg bg-primary-500 text-black font-medium hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAuthenticating ? (
                  <span className="flex items-center">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    {t("deviceCommands.authenticating")}
                  </span>
                ) : (
                  t("deviceCommands.sendCommand")
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render sending state
  if (viewState === "sending") {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className={`${bgColor} rounded-xl max-w-md w-full p-8 border ${borderColor} shadow-2xl`}>
          <div className="flex flex-col items-center justify-center py-8">
            {/* Spinner container */}
            <div className="relative w-20 h-20 mb-6">
              {/* Outer spinning ring */}
              <div className="absolute inset-0 rounded-full border-4 border-primary-500/30"></div>
              <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-primary-500 animate-spin"></div>
              {/* Inner icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <i className="ri-send-plane-line text-3xl text-primary-500"></i>
              </div>
            </div>
            <h3 className={`font-semibold text-lg ${textColor} mb-2`}>
              {t("deviceCommands.sending")}
            </h3>
            <p className={`text-center ${textSecondary} text-sm`}>
              {t("deviceCommands.pleaseWait")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Render result state (shows actual command status from socket updates)
  if (viewState === "result") {
    // Priorizar el estado del store (actualizado vía socket) sobre la respuesta inicial de la API
    const cmdStatus = currentCommandFromStore?.status || commandResponse?.command?.status;
    // El status es la única fuente de verdad para éxito/fallo (enum controlado), no el
    // campo `error` (texto libre que también transporta motivos no fatales como
    // queued_for_route). Así nunca mostramos "éxito verde" y "error rojo" a la vez.
    const isSuccess = cmdStatus ? isSuccessStatus(cmdStatus) : false;
    // En éxito no se muestra error (ni motivos no fatales ni el apiError de transporte).
    // En fallo, el texto específico del backend o el del HTTP; la descripción genérica del
    // estado se muestra aparte en el bloque de "info adicional".
    const backendError = currentCommandFromStore?.error || commandResponse?.command?.error;
    const cmdError = isSuccess ? null : (backendError || apiError);
    const requestId = currentCommandFromStore?.request_id || commandResponse?.command?.request_id;
    const statusColor = cmdStatus ? getStatusColor(cmdStatus) : null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className={`${bgColor} rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto border ${borderColor} shadow-2xl`}>
          {/* Header */}
          <div className={`flex items-center justify-between p-4 border-b ${borderColor}`}>
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg ${isSuccess ? "bg-green-500" : "bg-red-500"} flex items-center justify-center`}>
                <i className={`${isSuccess ? "ri-check-line" : "ri-close-line"} text-white text-lg`}></i>
              </div>
              <h2 className={`font-semibold text-lg ${textColor}`}>
                {isSuccess
                  ? t("deviceCommands.commandSent")
                  : t("deviceCommands.commandFailed")
                }
              </h2>
            </div>
            <button
              onClick={onClose}
              className={`p-2 ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"} rounded-lg transition-colors`}
            >
              <i className={`ri-close-line text-xl ${textColor}`}></i>
            </button>
          </div>

          <div className="p-6">
            {/* Nombre del comando */}
            <div className="text-center mb-4">
              <p className={`text-xs ${textSecondary} mb-1`}>
                {t("deviceCommands.command")}
              </p>
              <h3 className={`font-bold text-lg ${textColor}`}>
                {command.name}
              </h3>
            </div>

            {/* Estado del comando */}
            {cmdStatus && (
              <div className="flex justify-center mb-4">
                <span className={`px-4 py-2 rounded-lg text-sm font-medium ${statusColor?.bg} ${statusColor?.text}`}>
                  {getCommandStatusLabel(cmdStatus)}
                </span>
              </div>
            )}

            {/* Error message */}
            {cmdError && (
              <div className={`${isDarkMode ? "bg-red-900/30 text-red-400" : "bg-red-100 text-red-700"} px-3 py-2 rounded-lg text-sm mb-4 text-center`}>
                Error: <span className="font-bold">{cmdError}</span>
              </div>
            )}

            {/* Request ID */}
            {requestId && (
              <div className={`p-3 rounded-lg ${isDarkMode ? "bg-dark-surface" : "bg-gray-50"} mb-4`}>
                <p className={`text-[10px] ${textSecondary} mb-1`}>Request ID</p>
                <p className={`text-xs ${textColor} font-mono break-all`}>
                  {requestId}
                </p>
              </div>
            )}

            {/* Wait timeout indicator */}
            {commandResponse?.wait_timeout && (
              <div className={`${isDarkMode ? "bg-yellow-900/30 text-yellow-400" : "bg-yellow-100 text-yellow-700"} px-3 py-2 rounded-lg text-xs mb-4 text-center`}>
                {t("deviceCommands.waitTimeout")}
              </div>
            )}

            {/* Info adicional según el estado */}
            {cmdStatus && (
              <div className={`text-center ${textSecondary} text-xs mb-4`}>
                {cmdStatus === "pending_route" && t("deviceCommands.statusPendingRoute")}
                {cmdStatus === "queued_for_route" && t("deviceCommands.statusQueuedForRoute")}
                {cmdStatus === "queued" && t("deviceCommands.statusQueued")}
                {cmdStatus === "sending" && t("deviceCommands.statusSending")}
                {cmdStatus === "sent" && t("deviceCommands.statusSent")}
                {cmdStatus === "ack" && t("deviceCommands.statusAck")}
                {cmdStatus === "timeout" && t("deviceCommands.statusTimeout")}
                {cmdStatus === "offline" && t("deviceCommands.deviceOffline")}
                {cmdStatus === "unsupported" && t("deviceCommands.commandUnsupported")}
                {cmdStatus === "rejected" && t("deviceCommands.statusRejected")}
                {cmdStatus === "error" && t("deviceCommands.statusError")}
                {cmdStatus === "expired" && t("deviceCommands.statusExpired")}
                {cmdStatus === "write_failed" && t("deviceCommands.statusWriteFailed")}
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3 mt-6">
              {!isSuccess && (
                <button
                  onClick={() => {
                    // Reenviar el comando directamente
                    handleSendCommand();
                  }}
                  className={`flex-1 px-6 py-2.5 rounded-lg border ${borderColor} ${textColor} ${isDarkMode ? "hover:bg-dark-surface" : "hover:bg-gray-100"} transition-colors font-medium`}
                >
                  {t("deviceCommands.tryAgain")}
                </button>
              )}
              <button
                onClick={() => {
                  onSuccess();
                  onClose();
                }}
                className={`${isSuccess ? "w-full" : "flex-1"} px-6 py-2.5 rounded-lg bg-primary-500 text-black font-medium hover:bg-primary-400 transition-colors`}
              >
                {t("deviceCommands.close")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
